import { formatDateString } from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import {
  TRIAL_ENDING_SOON_DAYS,
  TRIAL_NEW_DAYS,
} from "@/v2-backoffice-client/license/trials/license.trials.constants"
import type {
  TrialCustomer,
  TrialKpis,
} from "@/v2-backoffice-client/license/trials/license.trials.utils"

const NAMED_LIMIT = 8

function plural(count: number, singular: string, pluralForm = `${singular}s`) {
  return `${formatNumber(count)} ${count === 1 ? singular : pluralForm}`
}

function nameList(names: string[]) {
  if (names.length <= NAMED_LIMIT) return names.join(", ")
  return `${names.slice(0, NAMED_LIMIT).join(", ")} and ${names.length - NAMED_LIMIT} more`
}

/**
 * The weekly post, written by the page: the key figures, who is ending or
 * overdue, who is new, who has gone quiet — and the link to the live report,
 * so the post is a pointer and never the source. Plain text with one line
 * per paragraph; it pastes into Slack as-is.
 */
export function formatTrialSummary(input: {
  customers: TrialCustomer[]
  kpis: TrialKpis
  reportUrl: string
  now?: Date
}) {
  const { customers, kpis, reportUrl, now = new Date() } = input
  const lines: string[] = []

  lines.push(`Trials — ${formatDateString(now.toISOString())}`)

  const products = kpis.byProduct
    .map((entry) => `${formatNumber(entry.count)} ${entry.product}`)
    .join(", ")
  lines.push(
    `${plural(kpis.customerCount, "customer")} on trial` +
      (kpis.licenseCount > 0
        ? ` (${plural(kpis.licenseCount, "license")}: ${products})`
        : "") +
      `. ${plural(kpis.peopleCount, "person", "people")} with a seat; ` +
      `${formatNumber(kpis.activePeople7d)} active in the last 7 days, ` +
      `${formatNumber(kpis.activePeople28d)} in the last 28, ` +
      `${formatNumber(kpis.silentPeople)} silent.`,
  )

  const byPulse = {
    engaged: customers.filter((customer) => customer.pulse === "engaged"),
    cooling: customers.filter((customer) => customer.pulse === "cooling"),
    silent: customers.filter((customer) => customer.pulse === "silent"),
    noUsers: customers.filter((customer) => customer.pulse === "no-users"),
  }
  lines.push(
    [
      `${formatNumber(byPulse.engaged.length)} engaged`,
      `${formatNumber(byPulse.cooling.length)} cooling`,
      `${formatNumber(byPulse.silent.length)} silent`,
      `${formatNumber(byPulse.noUsers.length)} without users`,
    ].join(" · "),
  )

  const endingSoon = customers.filter(
    (customer) => customer.clockState === "ending-soon",
  )
  if (endingSoon.length > 0) {
    lines.push(
      `Ending within ${TRIAL_ENDING_SOON_DAYS} days: ` +
        nameList(
          endingSoon.map(
            (customer) => `${customer.name} (${customer.clock?.facts.label})`,
          ),
        ) +
        ".",
    )
  }

  const overdue = customers.filter(
    (customer) => customer.clockState === "overdue",
  )
  if (overdue.length > 0) {
    lines.push(
      `Overdue, still on trial: ` +
        nameList(
          overdue.map(
            (customer) => `${customer.name} (${customer.clock?.facts.label})`,
          ),
        ) +
        ".",
    )
  }

  const fresh = customers.filter((customer) => customer.isNew)
  if (fresh.length > 0) {
    lines.push(
      `New in the last ${TRIAL_NEW_DAYS} days: ` +
        nameList(fresh.map((customer) => customer.name)) +
        ".",
    )
  }

  const quiet = [...byPulse.silent, ...byPulse.noUsers]
  if (quiet.length > 0) {
    lines.push(
      `Nobody using it: ` +
        nameList(quiet.map((customer) => customer.name)) +
        ".",
    )
  }

  lines.push(`Live report: ${reportUrl}`)
  return lines.join("\n")
}
